import { h, render } from 'preact'

import { EventActions, GiftCardActions, initApi, SerialTicketActions } from './actions'
import { ApiError } from './api-error'
import { assert } from './assert'
import { createFetchApi } from './fetch-api'
import { handleError } from './handle-error'
import './index.scss'
import { getLangByCode, shouldUseET, shouldUseFI, shouldUseLT, shouldUseLV } from './lang'
import { getEventRootProps, getGiftCardRootProps, getSerialTicketRootProps } from './props/root'
import { Root } from './root'
import { updateSessionExpireTimeout } from './session-expire'
import { CheckoutStep } from './types'
import type {
    Api,
    EventState,
    EventView,
    GiftCardState,
    GiftCardView,
    LangStrings,
    PluginSession,
    PluginTheme,
    SerialTicketState,
    SerialTicketView,
} from './types'

interface CommonParams {
    apiBaseUrl: string
    theme?: PluginTheme
    strings?: LangStrings

    // For development
    createMockApi?: (apiErrorClass: typeof ApiError) => Api
}

interface EventParams extends CommonParams {
    eventId?: string // Event UUID
    scheduleUrl: string
    hasNewsletterCheckbox: boolean
    newsletterCheckedByDefault: boolean
    selectedLang?: string
    termsLink?: string
}

interface GiftCardParams extends CommonParams {}
interface SerialTicketParams extends CommonParams {}

const Kinola = {
    render: async (container: HTMLElement, params: EventParams) => {
        // Create API instance
        let apiInstance: Api
        if (params.createMockApi) {
            apiInstance = params.createMockApi(ApiError)
            initApi(apiInstance)
        } else {
            apiInstance = createFetchApi(params.apiBaseUrl)
            initApi(apiInstance)
        }

        // Render into a shadow DOM so our CSS wouldn't affect the parent DOM and vice versa
        const shadowRoot = container.attachShadow({ mode: 'open' })

        addFontsLink(container)

        const selectedLang =
            params.selectedLang ??
            (shouldUseLV()
                ? 'lv'
                : shouldUseET()
                  ? 'et'
                  : shouldUseFI()
                    ? 'fi'
                    : shouldUseLT()
                      ? 'lt'
                      : 'en')

        // Initialize with base languages - now using the getLangByCode function
        const baseLanguages = {
            ...getLangByCode(selectedLang),
            ...params.strings,
        }

        const state: EventState = {
            eventUuid: params.eventId,
            selectedLang,
            theme: params.theme ?? 'light',
            isAddProductsFlow: !params.eventId,
            selectedTicket: {},
            hideDiscountCards: false,
            isInitializing: true,
            sessionMinutes: 0,
            sessionEndTime: 0,
            lang: baseLanguages,
            details: {
                startDate: '',
                datetime: {
                    date: '',
                    time: '',
                },
            },
            production: {
                id: '',
                name: '',
                image: {
                    srcset: '',
                },
            },
            floorplan: {
                width: 0,
                height: 0,
                grids: [],
            },
            labels: {
                intro: {
                    title: '',
                    text: '',
                    venueName: '',
                },
                tickets: {
                    subtitle: '',
                },
                productSelection: '',
                paySection: '',
            },
            hasNewsletterCheckbox: params.hasNewsletterCheckbox,
            newsletterCheckedByDefault: params.newsletterCheckedByDefault ?? false,
            termsLink: params.termsLink ? params.termsLink : '',
            status: 'checkout',
            paymentOptions: [],
            activeCheckoutStep: CheckoutStep.TICKETS,
            productCategories: new Map(),
            products: [],
            productsPageNumber: 1,
            paymentFailed: false,
            scheduleUrl: params.scheduleUrl,
            completionData: {
                ticketsTitle: '',
                ticketLines: [],
                ticketsSentText: '',
                ticketsDownloadText: '',
                ticketsButton: {
                    url: '',
                    text: '',
                },
                invoiceSentText: '',
                invoiceDownloadText: '',
                invoiceButton: {
                    url: '',
                    text: '',
                },
                contactInfo: '',
            },
            ticketTypes: [],
            calc: {
                timestamp: 0,
                tickets: [],
                products: [],
                serialTicketPayments: [],
                giftCardPayments: [],
                total: 0,
                partialPayments: [],
                toPay: 0,
            },
            seats: {
                freeCount: 0,
                unavailable: [],
            },
            cards: {
                code: '',
            },
            validationErrors: {},
        }

        const view: EventView = {
            state,
            update: () => {
                // sessionEndTime may have changed
                updateSessionExpireTimeout(view)

                const props = getEventRootProps(view)
                render(h(Root, props), shadowRoot)
            },
            timeouts: new Map(),
        }

        // Initial render
        view.update()

        // Start loading general data
        const generalDataPromise = EventActions.loadGeneralData(view)
        let eventsPromise: Promise<void> | undefined

        if (state.isAddProductsFlow) {
            eventsPromise = EventActions.loadEventsData(view)
        }

        if (!state.eventUuid) {
            await EventActions.loadSession(view)
        }

        if (!state.eventUuid) {
            const urlParams = new URLSearchParams(window.location.search)
            const eventUuid = urlParams.get('kinolaEventUuid')

            if (eventUuid) {
                const session: PluginSession = { eventUuid }

                const [rawRowNumber, rawSeatNumber] = urlParams.get('kinolaSeat')?.split('-') ?? []
                const rowNumber = parseInt(rawRowNumber)
                const seatNumber = parseInt(rawSeatNumber)

                if (!isNaN(rowNumber) && !isNaN(seatNumber)) {
                    session.rowNumber = rowNumber
                    session.seatNumber = seatNumber
                }

                try {
                    await EventActions.updateSession(session)
                    state.eventUuid = eventUuid
                    state.session = session
                } catch (error) {
                    handleError(view, error as Error)
                }
            }
        }

        if (state.eventUuid) {
            await EventActions.loadEvent(view)

            if (state.paymentFailed) {
                // Open summary tab so the "payment failed" message would be visible
                state.activeCheckoutStep = CheckoutStep.SUMMARY
            }

            if (state.isAddProductsFlow) {
                await eventsPromise
                assert(state.eventsData)
                const event = state.eventsData.find((e) => e.eventUuid === state.eventUuid)

                if (event) {
                    state.selectedTicket = {
                        productionUuid: event.productionUuid,
                        eventUuid: event.eventUuid,
                        row: state.session?.rowNumber ?? undefined,
                        seat: state.session?.seatNumber ?? undefined,
                    }
                }
            }
        }

        // Wait for general data
        await generalDataPromise

        // Wait for any events data to be loaded
        await eventsPromise

        state.isInitializing = false
        view.update()

        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                // The page was restored after navigating back to it.
                // This usually happens when pressing 'Back' after initiating payment,
                // in which case the booked seats have been cleared, but the UI is
                // showing outdated state. The simplest solution is to force a refresh.
                window.location.reload()
            }
        })
    },
    renderGiftCard: async (container: HTMLElement, params: GiftCardParams) => {
        // Create API instance
        let apiInstance: Api
        if (params.createMockApi) {
            apiInstance = params.createMockApi(ApiError)
            initApi(apiInstance)
        } else {
            apiInstance = createFetchApi(params.apiBaseUrl)
            initApi(apiInstance)
        }

        // Render into a shadow DOM so our CSS wouldn't affect the parent DOM and vice versa
        const shadowRoot = container.attachShadow({ mode: 'open' })

        addFontsLink(container)

        // Determine language based on URL detection
        const selectedLang = shouldUseLV()
            ? 'lv'
            : shouldUseET()
              ? 'et'
              : shouldUseFI()
                ? 'fi'
                : shouldUseLT()
                  ? 'lt'
                  : 'en'

        // Initialize with appropriate language
        const baseLanguages = {
            ...getLangByCode(selectedLang),
            ...params.strings,
        }

        const state: GiftCardState = {
            theme: params.theme ?? 'light',
            lang: baseLanguages,
            amount: '',
            email: '',
            validationErrors: {},
        }

        const view: GiftCardView = {
            state,
            update: () => {
                const props = getGiftCardRootProps(view)
                render(h(Root, props), shadowRoot)
            },
        }

        // Initial render
        view.update()

        try {
            state.data = await GiftCardActions.getData()
            state.amount = String(state.data.ticketPrice)
            view.update()
        } catch (error) {
            handleError(view, error as Error)
        }
    },
    renderSerialTicket: async (container: HTMLElement, params: SerialTicketParams) => {
        // Create API instance
        let apiInstance: Api
        if (params.createMockApi) {
            apiInstance = params.createMockApi(ApiError)
            initApi(apiInstance)
        } else {
            apiInstance = createFetchApi(params.apiBaseUrl)
            initApi(apiInstance)
        }

        // Render into a shadow DOM so our CSS wouldn't affect the parent DOM and vice versa
        const shadowRoot = container.attachShadow({ mode: 'open' })

        addFontsLink(container)

        // Determine language based on URL detection
        const selectedLang = shouldUseLV()
            ? 'lv'
            : shouldUseET()
              ? 'et'
              : shouldUseFI()
                ? 'fi'
                : shouldUseLT()
                  ? 'lt'
                  : 'en'

        // Initialize with appropriate language
        const baseLanguages = {
            ...getLangByCode(selectedLang),
            ...params.strings,
        }

        const state: SerialTicketState = {
            theme: params.theme ?? 'light',
            lang: baseLanguages,
            typeId: 0,
            email: '',
            validationErrors: {},
        }

        const view: SerialTicketView = {
            state,
            update: () => {
                const props = getSerialTicketRootProps(view)
                render(h(Root, props), shadowRoot)
            },
        }

        // Initial render
        view.update()

        try {
            state.data = await SerialTicketActions.getData()
            if (state.data.types && state.data.types.length > 0) {
                state.typeId = state.data.types[0].id
            }
            view.update()
        } catch (error) {
            handleError(view, error as Error)
        }
    },
}

const addFontsLink = (container: HTMLElement) => {
    // @font-face doesn't work inside the shadow DOM, so we must use it in the parent
    const fontsLink = document.createElement('link')
    fontsLink.rel = 'stylesheet'
    fontsLink.href =
        'https://fonts.googleapis.com/css?family=Poppins:400,400i,600,600i&display=swap'
    container.appendChild(fontsLink)
}

declare global {
    interface Window {
        Kinola: typeof Kinola
        dataLayer: Array<unknown>
    }
}

window.Kinola = Kinola
